/*
 * From https://www.redblobgames.com/maps/terrain-from-noise/
 * Copyright 2022 Red Blob Games <redblobgames@gmail.com>
 * License: Apache-2.0 <http://www.apache.org/licenses/LICENSE-2.0.html>
 *
 * Diagrams for islands.html
 */

import SvgRemappingChart from "./visualize-reshaper";
import {CanvasDistanceChart, CanvasIslandDistanceChart} from "./visualize-distance";
import Map2D from "./map2d";
import Renderer2D from "./render2d";
import {SEED, mixNoise} from "./noise-utils";
import * as Colormap from "./colormap";

function clamp(x: number, lo: number, hi: number): number {
    if (x < lo) x = lo;
    if (x > hi) x = hi;
    return x;
}

function quadraticBezier(p0: number, p1: number, p2: number, t: number): number {
    return p1 + (1-t)**2 * (p0-p1) + t**2 * (p2-p1);
}

// These are all shaping functions I have used and also some I've designed without using them yet
const reshapingFns: Array<[string, (d: number) => (e: number) => number]> = [
    ["Input", _ => e => e],
    ["Flat", d => _ => d],
    ["Linear", d => e => (e+d)/2],
    ["LinearSteep", d => e => { let lo = clamp(d-1/2, 0, 1), hi = clamp(d+1/2, 0, 1); return (1-e)*lo + e*hi; }],
    ["Clamped", d => e => clamp(e, d-0.49, d+0.49)],
    ["Smooth", d => e => quadraticBezier(Math.max(0, d-1/2), 1/2, Math.min(1, d+1/2), e)],
    ["Smooth2", d => e => quadraticBezier(Math.max(0, d**2-1/2), 1/2, Math.min(1, (1-(1-d)**2)+1/2), e)],
    ["Smooth3", d => e => quadraticBezier(Math.max(0, d**3-1/2), 1/2, Math.min(1, (1-(1-d)**3)+1/2), e)],
    ["ClampedLess", d => e => clamp(e, d**2 - 0.45, (1-(1-d)**2)+0.45)],
    // ["SmoothLow*", d => e => quadraticBezier(0, 1/2, Math.min(1, d+1/2), e)],
    // ["Smooth3Low*", d => e => quadraticBezier(0, 1/2, Math.min(1, (1-(1-d)**3)+1/2), e)],
    // ["Archipelago*", d => e => {
    //    let d2 = 1 - 2 * Math.abs(d - 0.5); // like ridged noise
    //    // note: the 5/12 was originally 1/2 but it produced too much land
    //    // for an "archipelago"
    //    return quadraticBezier(Math.max(0, d2-3/4), 5/12, Math.min(1, d2+1/2), e);
    //}],
];

// Many of these are from KdotJPG in 2021
// https://old.reddit.com/r/proceduralgeneration/comments/kaen7h/new_video_on_procedural_island_noise_generation/gfjmgen/
const distanceFns: Array<[string, (nx: number, ny: number) => number]> = [
    ["Euclidean", (nx, ny) => Math.hypot(nx, ny) / Math.sqrt(2)],
    ["Euclidean²", (nx, ny) => (nx*nx + ny*ny) / 2],
    ["Hyperboloid", (nx, ny) => (Math.hypot(nx, ny, 0.2) - 0.2) / (Math.hypot(1, 1, 0.2) - 0.2)], /* 0.2 could be other constants */
    ["Squircle", (nx, ny) => Math.sqrt(nx**4 + ny**4) / Math.sqrt(2)],
    ["SquareBump", (nx, ny) => 1 - (1-nx*nx) * (1-ny*ny)],
    ["TrigProduct", (nx, ny) => 1 - Math.cos(nx*Math.PI/2) * Math.cos(ny*Math.PI/2)],
    ["Diagonal", (nx, ny) => Math.max(Math.abs(nx), Math.abs(ny))],
    ["Manhattan", (nx, ny) => (Math.abs(nx) + Math.abs(ny)) / 2],
    // ["Blob*", (nx, ny) => Math.hypot(nx, ny) / Math.sqrt(2) * 3.2 / (4 - Math.sin(5 * Math.atan2(ny, nx)))],
];

function visualizeReshapingFunctions() {
    const svgLandAndWater = {prefixSvg: `
         <rect fill="hsl(40, 20%, 95%)" width="1" height="0.5"/>
         <rect fill="hsl(240, 30%, 90%)" width="1" y="0.5" height="0.5"/>
    `};

    const numIntermediateSteps = 9;
    let parent: HTMLElement = document.querySelector("#charts-island-reshaping");
    parent.style.gridTemplateColumns = `8em repeat(${numIntermediateSteps}, minmax(0, 1fr))`;

    // Top labels
    let leftTitle = document.createElement('span');
    leftTitle.setAttribute('style', "grid-column: 2 / span 2; align-self: center");
    leftTitle.innerText = "Border of map (want water)";
    let rightTitle = document.createElement('span');
    rightTitle.setAttribute('style', "grid-column: -3 / span 2; align-self: center");
    rightTitle.innerText = "Center of map (want land)";
    parent.appendChild(leftTitle);
    parent.appendChild(rightTitle);
    
    
    for (let [name, fn] of reshapingFns) {
        let text = document.createElement('span');
        text.innerText = name;
        parent.appendChild(text);
        
        for (let i = 0; i < numIntermediateSteps; i++) {
            let svg = document.createElementNS("http://www.w3.org/2000/svg", 'svg');
            parent.appendChild(svg);
            new SvgRemappingChart(svg, svgLandAndWater).setFn(fn(i/(numIntermediateSteps-1)));
        }
    }
}

function visualizeDistanceFunctions() {
    let parent: HTMLElement = document.querySelector("#charts-island-distance");
    parent.style.gridTemplateColumns = `repeat(${distanceFns.length}, minmax(0, 1fr))`;
    for (let [name, fn] of distanceFns) {
        let div = document.createElement('div');
        let label = document.createElement('div');
        label.innerText = name;
        let canvas = document.createElement('canvas');
        div.appendChild(canvas);
        div.appendChild(label);
        parent.appendChild(div);
        CanvasIslandDistanceChart(canvas, fn);
        // TODO: also plot how much is constrained, by combining
        // distance and reshape fn
    }
}

function visualizeNoiseRange() {
    // Some of this code copied form visualizeCombinations
    const size = 200;
    const LOW = [90, 0, 0, 255], HIGH = [190, 255, 255, 255];

    let parent: HTMLElement = document.querySelector("#islands-range");
    parent.style.gridTemplateColumns = `8em repeat(${distanceFns.length}, minmax(0, 1fr))`;

    let elevation = new Map2D(size, size);
    function generateNoise(seed=SEED) {
        mixNoise(elevation, [1, 2/3, 1/2, 1/3, 1/4], 2, seed);
    }
    generateNoise();
    let colors = Colormap.waterlevel(Colormap.greengray());
    colors.setLevel(127.5);

    // Top labels
    parent.appendChild(document.createElement('span'));

    for (let [distanceName, _] of distanceFns) {
        let text = document.createElement('span');
        text.innerText = distanceName;
        parent.appendChild(text);
    }

    for (let [reshaperName, reshaperFn] of reshapingFns) {
        // Left labels
        let text = document.createElement('span');
        text.innerText = reshaperName;
        parent.appendChild(text);
        
        for (let [distanceName, distanceFn] of distanceFns) {
            let id = `range-${distanceName}-${reshaperName}`;
            let canvas = document.createElement('canvas');
            canvas.setAttribute('width', size.toString());
            canvas.setAttribute('height', size.toString());
            canvas.setAttribute('id', id);

            function colorFn(nx, ny, output_color) {
                let d = distanceFn(nx, ny);
                let range = reshaperFn(1-d)(1) - reshaperFn(1-d)(0);
                if (range < 0.4) {
                    for (let channel = 0; channel < 3; channel++) {
                        output_color[channel] = 0;
                    }
                    output_color[3] = 255;
                } else {
                    for (let channel = 0; channel < 4; channel++) {
                        output_color[channel] = (1-range) * LOW[channel] + range * HIGH[channel];
                    }
                }
            }
    
            CanvasDistanceChart(canvas, colorFn);
            parent.appendChild(canvas);
        }
    }
}

function visualizeCombinations() {
    const size = 200;
    let updaters = []; // functions to call to re-render

    let parent: HTMLElement = document.querySelector("#islands");
    parent.style.gridTemplateColumns = `8em repeat(${distanceFns.length}, minmax(0, 1fr))`;

    let elevation = new Map2D(size, size);
    let height = new Map2D(size, size);
    function generateNoise(seed=SEED) {
        mixNoise(elevation, [1, 2/3, 1/2, 1/3, 1/4], 2, seed);
    }
    generateNoise();
    let colors = Colormap.waterlevel(Colormap.greengray());
    colors.setLevel(127.5);
    
    // Top labels
    parent.appendChild(document.createElement('span'));

    for (let [distanceName, _] of distanceFns) {
        let text = document.createElement('span');
        text.innerText = distanceName;
        parent.appendChild(text);
    }
    
    for (let [reshaperName, reshaperFn] of reshapingFns) {
        // Left labels
        let text = document.createElement('span');
        text.innerText = reshaperName;
        parent.appendChild(text);
        
        for (let [distanceName, distanceFn] of distanceFns) {
            let id = `island-${distanceName}-${reshaperName}`;
            let canvas = document.createElement('canvas');
            canvas.setAttribute('width', size.toString());
            canvas.setAttribute('height', size.toString());
            canvas.setAttribute('id', id);

            parent.appendChild(canvas);

            let renderer = new Renderer2D(id, height, colors);
            
            function calculate() {
                for (let y = 0; y < elevation.h; y++) {
                    for (let x = 0; x < elevation.w; x++) {
                        let nx = 2 * x/elevation.w - 1;
                        let ny = 2 * y/elevation.h - 1;
                        let d = distanceFn(nx, ny);
                        let e = elevation.get(x, y);
                        e = reshaperFn(1-d)(e);
                        height.set(x, y, e);
                    }
                }
                renderer.draw();
            }
            calculate();
            updaters.push(calculate);
        }
    }

    function randomize() {
        generateNoise(Math.random() * 100000 | 0);
        for (let update of updaters) {
            update();
        }
    }
    document.getElementById('recalculate-islands')
        .addEventListener('click', randomize);
}

visualizeReshapingFunctions();
visualizeNoiseRange();
visualizeDistanceFunctions();
visualizeCombinations();
